import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, 
  Star, 
  BookOpen, 
  Headphones, 
  Printer, 
  Cloud, 
  Share2,
  Heart,
  Sparkles,
  ArrowRight,
  Shield,
  Lock,
  Gift
} from 'lucide-react';

const features = [
  { icon: BookOpen, text: "6 transformative chapters" },
  { icon: Headphones, text: "Audio narration for each chapter" },
  { icon: Printer, text: "Print-friendly PDF export" },
  { icon: Cloud, text: "iCloud backup & restore" },
  { icon: Share2, text: "Shareable quote graphics" },
  { icon: Heart, text: "Interactive journal prompts" },
];

const testimonials = [
  {
    quote: "This guide gave me permission to grieve and hope at the same time. For the first time, I believe healing is possible.",
    author: "Sarah M."
  },
  {
    quote: "The journal prompts helped me process feelings I'd buried for years. I finally feel seen and understood.",
    author: "Rebecca T."
  },
  {
    quote: "I've read many books on healing, but this is the first one that truly speaks to my faith journey.",
    author: "Grace L."
  }
];

export const SalesPage = () => {
  const GUMROAD_EBOOK = 'https://mcgowancee.gumroad.com/l/ofumns';
  const GUMROAD_FULL = 'https://mcgowancee.gumroad.com/l/dvmykj';

  return (
    <div data-testid="sales-page" className="min-h-screen bg-cream">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1705905119187-c6986d4d44db?crop=entropy&cs=srgb&fm=jpg&q=85)` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-cream/60 via-cream/80 to-cream" />
        
        <div className="relative max-w-4xl mx-auto px-6 pt-12 pb-16 md:pt-20 md:pb-24">
          <div className="text-center animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-gold">Faith-Based Healing Guide</span>
            </div>
            
            {/* Ebook Cover Image */}
            <div className="mb-8">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/98f4c0a7-37e3-4eee-b45d-3c901b2920ec/images/0470b11727d32ba005664c3bc8b17f0ec5bd3d699a6a10c5afd06c63414fb5d3.png"
                alt="Breakthrough - A Faith-Based Guide to Healing by Janelle McGowan-Charles"
                data-testid="ebook-cover-image"
                className="w-48 md:w-56 mx-auto rounded-lg shadow-2xl"
              />
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-charcoal mb-4">
              Breakthrough
            </h1>
            <p className="text-lg md:text-xl text-charcoal-light max-w-2xl mx-auto mb-8">
              A transformative guide for all women ready to break free from 
              shame, guilt, and limiting beliefs after trauma
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <div className="flex items-center gap-2 text-sm text-charcoal-light">
                <Shield className="w-4 h-4 text-sage" />
                <span>Secure Checkout</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-charcoal-light">
                <Lock className="w-4 h-4 text-sage" />
                <span>Instant Access</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-charcoal-light">
                <Heart className="w-4 h-4 text-sage" />
                <span>Made with Love</span>
              </div>
            </div>
            <p className="text-sm text-sage font-medium">
              You will instantly receive your Purpose Reclaim ebook download and bonus resources via secure checkout
            </p>
          </div>
        </div>
      </div>

      {/* What's Inside */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="font-heading text-3xl text-charcoal text-center mb-10">
          What's Inside
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm"
            >
              <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-6 h-6 text-sage" />
              </div>
              <span className="text-charcoal font-medium">{feature.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Chapter Preview */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h2 className="font-heading text-3xl text-charcoal text-center mb-10">
          Your Journey Through 6 Chapters
        </h2>
        <div className="space-y-4">
          {[
            { num: 1, title: "The Fog of Trauma", desc: "Acknowledging where you are" },
            { num: 2, title: "Breaking Free from Shame", desc: "Releasing what was never yours" },
            { num: 3, title: "Releasing Guilt & Self-Blame", desc: "Understanding what wasn't your fault" },
            { num: 4, title: "Escaping Victim Mentality", desc: "From survivor to overcomer" },
            { num: 5, title: "The Journey to Forgiveness", desc: "Finding freedom through letting go" },
            { num: 6, title: "Seeing Your True Potential", desc: "Embracing God's vision for your life" },
          ].map((chapter) => (
            <div 
              key={chapter.num}
              className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm"
            >
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-lg text-gold">{chapter.num}</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-charcoal">{chapter.title}</h3>
                <p className="text-sm text-charcoal-light">{chapter.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-gradient-to-r from-sky-light/30 to-sage-light/20 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-10">
            Words from Sisters on the Journey
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="text-charcoal/80 italic mb-4">"{item.quote}"</p>
                <p className="text-sm font-medium text-sage">— {item.author}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="max-w-5xl mx-auto px-6 py-16" id="pricing">
        <h2 className="font-heading text-3xl text-charcoal text-center mb-4">
          Choose Your Path
        </h2>
        <p className="text-charcoal-light text-center mb-10">
          Instant digital access — start your healing journey today
        </p>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {/* Option 1 — Free Resources */}
          <div 
            data-testid="plan-free-resources"
            className="relative p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <h3 className="font-heading text-xl text-charcoal mb-1">Free Resources</h3>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-4xl font-heading text-charcoal">£0</span>
              <span className="text-charcoal-light text-sm">free</span>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                Chapter 1 preview
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                5 Lies Survivors Believe guide
              </li>
            </ul>
            <Link
              to="/free"
              data-testid="get-free-resources-btn"
              className="block w-full py-3 bg-gold/10 text-gold rounded-full font-medium text-sm hover:bg-gold/20 transition-all text-center"
            >
              Get Free Resources
            </Link>
          </div>

          {/* Option 2 — Ebook Only */}
          <div 
            data-testid="plan-ebook-only"
            className="relative p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <h3 className="font-heading text-xl text-charcoal mb-1">Ebook Only</h3>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-4xl font-heading text-charcoal">£17</span>
              <span className="text-charcoal-light text-sm">one-time</span>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-sage flex-shrink-0" />
                Full Breakthrough Ebook
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-sage flex-shrink-0" />
                Kindle version
              </li>
            </ul>
            <div className="flex items-center justify-center gap-1.5 mb-3 text-xs text-charcoal-light">
              <Lock className="w-3 h-3" />
              <span>Secure checkout via Gumroad</span>
            </div>
            <a
              href={GUMROAD_EBOOK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="buy-ebook-only-btn"
              className="block w-full py-3 bg-sage/10 text-sage rounded-full font-medium text-sm hover:bg-sage/20 transition-all text-center"
            >
              Get Started
            </a>
          </div>

          {/* Option 3 — Full Package (Most Popular) */}
          <div 
            data-testid="plan-full-package"
            className="relative p-6 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all ring-2 ring-gold scale-[1.02]"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold text-white text-xs font-bold rounded-full uppercase tracking-wider">
              Most Popular
            </div>
            <h3 className="font-heading text-xl text-charcoal mb-1">Full Package</h3>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-4xl font-heading text-charcoal">£40</span>
              <span className="text-charcoal-light text-sm">one-time</span>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                Full Ebook + Kindle + PDF
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                Audio version
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                Prayer & Journaling Workbook
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                Healing Worksheets
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                5 Lies mini-guide
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                12-Week Transformation Journal
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                30-Day Prayer Guide
              </li>
              <li className="flex items-center gap-2 text-sm text-charcoal font-medium">
                <Check className="w-4 h-4 text-gold flex-shrink-0" />
                Purpose Reclaimed Workshop access
              </li>
            </ul>
            <div className="flex items-center justify-center gap-1.5 mb-3 text-xs text-charcoal-light">
              <Lock className="w-3 h-3" />
              <span>Secure checkout via Gumroad</span>
            </div>
            <a
              href={GUMROAD_FULL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="buy-full-package-btn"
              className="block w-full py-3 bg-gold text-white rounded-full font-medium text-sm hover:bg-gold/90 transition-all shadow-md hover:shadow-lg text-center"
            >
              Get Full Access
            </a>
          </div>
        </div>

        <p className="text-sm text-charcoal-light text-center mt-8">
          Secure payment powered by Gumroad — taxes handled automatically
        </p>
      </div>

      {/* Free Preview CTA */}
      <div className="max-w-3xl mx-auto px-6 pb-16">
        <div className="p-8 bg-gradient-to-r from-sky-light/30 to-sage-light/20 rounded-2xl text-center">
          <h3 className="font-heading text-2xl text-charcoal mb-3">
            Not sure yet?
          </h3>
          <p className="text-charcoal-light mb-6">
            Try Chapter 1 and our "5 Lies Survivors Believe" guide completely free
          </p>
          <Link
            to="/free"
            data-testid="try-free-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-sage border border-sage/20 rounded-full font-medium hover:bg-sage/5 transition-all"
          >
            Get Free Preview
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white/50 border-t border-stone-100 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Link to="/" className="font-accent text-2xl text-sage hover:text-sage-dark transition-colors">
            Breakthrough
          </Link>
          <p className="text-sm text-charcoal-light mt-2">A Faith-Based Guide to Healing</p>
          <p className="text-sm text-charcoal-light mt-1">By Janelle McGowan-Charles</p>
          <p className="text-xs text-charcoal-light/70 mt-1">Purpose Reclaimed Coaching</p>
          <p className="text-xs text-charcoal-light/60 mt-3">© {new Date().getFullYear()} Janelle McGowan-Charles, Purpose Reclaimed Coaching. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
